<template>
  <s-sidebar margin-top="var(--topbar-height)" :route="route">
    <template #items>
      <!-- Ordem por frequência de uso: a rotina da escola (agenda, planos, turmas) no topo, a
           gestão da rede no meio, o conteúdo e a administração no fim. Os divisores separam os
           grupos e só aparecem para a equipe: Aluno e Suporte veem um item só. -->
      <s-sidebar-item v-if="equipe"
        route="/"
        label="Home"
        icon="mdiHome"
      />

      <s-sidebar-item v-if="!isSuporte"
        route="/agenda"
        label="Agenda"
        icon="mdiCalendarMonthOutline"
      />

      <s-sidebar-item v-if="equipe"
        route="/planos"
        label="Planos de Aula"
        icon="mdiBookOpenVariant"
      />

      <s-sidebar-item v-if="equipe"
        route="/turmas"
        label="Turmas"
        icon="mdiAccountGroup"
      />

      <span v-if="equipe" class="divisor" role="separator" />

      <s-sidebar-item v-if="equipe"
        route="/escolas"
        label="Escolas"
        icon="mdiSchool"
      />

      <s-sidebar-item v-if="equipe || isSuporte"
        route="/equipamentos"
        label="Equipamentos"
        icon="mdiTabletCellphone"
      />

      <s-sidebar-item v-if="equipe"
        route="/relatorios"
        label="Relatórios"
        icon="mdiChartBar"
      />

      <span v-if="equipe" class="divisor" role="separator" />

      <!-- Conteúdo que vai para os equipamentos e para a equipe. Livros, episódios e coleções
           só para quem mantém o acervo (mesma regra de /acervo em ~/constants/roles). -->
      <s-sidebar-item-group v-if="equipe" label="Acervo" icon="mdiBookshelf">
        <template v-if="isAdmin || isGestor">
          <s-sidebar-sub-item
            route="/acervo"
            label="Livros"
          />
          <s-sidebar-sub-item
            route="/acervo/episodios"
            label="Episódios"
          />
          <s-sidebar-sub-item
            route="/acervo/colecoes"
            label="Coleções"
          />
        </template>
        <s-sidebar-sub-item
          route="/aplicativos"
          label="Aplicativos"
        />
        <s-sidebar-sub-item
          route="/cursos"
          label="Cursos"
        />
        <s-sidebar-sub-item
          route="/guias"
          label="Guias de Uso"
        />
      </s-sidebar-item-group>

      <template v-if="isAdmin">
        <s-sidebar-item-group label="Usuários" icon="mdiAccountMultipleOutline">
          <s-sidebar-sub-item
            route="/usuarios"
            label="Usuários"
          />
          <s-sidebar-sub-item
            route="/usuarios/tipos"
            label="Tipos"
          />
        </s-sidebar-item-group>
      </template>

      <template v-else>
        <s-sidebar-item v-if="equipe"
          route="/usuarios"
          label="Usuários"
          icon="mdiAccountMultipleOutline"
        />
      </template>

      <s-sidebar-item v-if="isAdmin"
        route="/organizacao"
        label="Organização"
        icon="mdiDomain"
      />
    </template>

    <template #actions>
      <button
        type="button"
        class="tema"
        :aria-label="themeStore.escuro ? 'Usar tema claro' : 'Usar tema escuro'"
        @click="themeStore.toggle()"
      >
        <s-icon :icon="themeStore.escuro ? 'mdiWeatherNight' : 'mdiWhiteBalanceSunny'" size="small" :color="themeStore.escuro ? 'primary' : 'warning'" />
        <span class="tema-rotulo">Tema {{ themeStore.escuro ? 'escuro' : 'claro' }}</span>
      </button>
    </template>
  </s-sidebar>
</template>

<script setup lang="ts">
import { SSidebar, SSidebarItem, SSidebarItemGroup, SSidebarSubItem } from '@sieg-ad/componentes-vue';
import { useThemeStore } from '~/stores/theme';
import { useAuthStore } from '~/stores/auth';

const authStore = useAuthStore();
const themeStore = useThemeStore();
const { isAdministrador: isAdmin, isGestor, isSuporte } = storeToRefs(authStore);
const route = useRoute();

//? `equipe` é o menu de quem opera as escolas. Fora dele: o Aluno (só Agenda) e o Suporte
//? (só Equipamentos) — a mesma fronteira que o guard global aplica em ~/constants/roles.
const equipe = computed(() =>
  authStore.isAdministrador || authStore.isGestor || authStore.isDocente);
</script>

<style scoped>
/* Mesmo desenho dos s-sidebar-item. A margem negativa anula o padding lateral do rodapé do
   s-sidebar, para o ícone cair na mesma coluna dos ícones do menu. */
.tema {
  display: flex;
  align-items: center;
  gap: var(--s-sidebar-item-gap);
  width: calc(100% + 0.6rem);
  margin: 0 -0.3rem 0.25rem;
  padding: var(--s-sidebar-item-padding-y) var(--s-sidebar-item-padding-x);
  border: 0;
  border-radius: var(--s-sidebar-item-radius);
  background: none;
  color: var(--text-primary);
  font: inherit;
  font-size: var(--s-sidebar-item-font-size);
  font-weight: var(--s-sidebar-item-font-weight);
  white-space: nowrap;
  cursor: pointer;
  transition: background-color var(--ts-fast) ease;
}

.tema:hover {
  background-color: var(--s-sidebar-hover);
}

.tema:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: -2px;
}

.tema-rotulo {
  overflow: hidden;
}

.divisor {
  display: block;
  height: 1px;
  margin: 0.4rem 0.6rem 0.65rem;
  background: var(--border-primary);
}
</style>
